import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "A message from your future self",
  "description": "",
  "published": "2012-01-06T00:00:00.000Z",
  "redirect_from": ["/blog/a-message-from-your-future-self/swizec/3377"],
  "categories": "Functional programming, Haskell, Implementations, Moving average, Programming, Tools, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`About two weeks ago I watched a TED talk on the battle between one's present and future self. The idea being that a lot of our problems can be summed up to this: Your present self is here, he wants cool things, your future self is greatly affected by these decisions, but he can't do anything about it.`}</p>
    <p>{`Over the holidays I gave Future Swizec a voice. Every three days he sends me an email to tell me what sort of financial situation he will find himself in after two weeks of Present Swizec doing stupid shit. That's not very far into the future, but it's just enough to do something about it, while not being too far away to think `}<em parentName="p">{`Meh, that's in ten years, I'll deal with it nine years from now.`}</em></p>
    <p>{`If you want to give Future You this kind of voice have a look at my `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/Personal-Runway"
      }}>{`Personal Runway project on github`}</a>{`. If you think this is a cool idea, but don't want to set it up yourself - email me! I might just make this `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Proof_of_concept",
        "title": "Proof of concept"
      }}>{`proof of concept`}</a>{` into a minimum viable product if there is any interest :)`}</p>
    <p>{`I first ran it when I had just finished paying all my bills, which was a huge expense, so Future Swizec sent me this email:`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/01/Selection_047.png",
        "alt": "Future Swizec",
        "title": "Future Swizec"
      }}></img></p>
    <p>{`Maybe he should be less happy about only having 5euro, but right now he's super every time his balance isn't negative. Perhaps Future Swizec needs a gray area.`}</p>
    <h2 {...{
      "id": "how-it-works"
    }}>{`How it works`}</h2>
    <p>{`Right now the predictions are somewhat rudimentary, for instance I don't take into account the fact money doesn't magically show up in my account after every billable hour. Another thing not taken into account are periodic expenses, super large spikes also throw it off somewhat.`}</p>
    <p>{`The basic idea is this:`}</p>
    <ol>
      <li parentName="ol">{`Smooth out the data with a `}<a parentName="li" {...{
          "href": "http://en.wikipedia.org/wiki/Moving_average",
          "title": "Moving average"
        }}>{`rolling average`}</a>{` to get a curve`}</li>
      <li parentName="ol">{`Calculate an `}<a parentName="li" {...{
          "href": "http://en.wikipedia.org/wiki/Expected_value",
          "title": "Expected value"
        }}>{`expected value`}</a>{` for the next day after known data`}</li>
      <li parentName="ol">{`Expand the window for expected value by two (one into the future, one into the past) and calculate the next expected value`}</li>
    </ol>
    <p>{`Weights are calculated according to the simplest `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Series_%28mathematics%29",
        "title": "Series (mathematics)"
      }}>{`infinite series`}</a>{` I could think of that always sums to one no matter how many elements are needed. But I also split it in half and shuffle it about at the pivot, so the strongest weight is moved back in the data when calculating days. So in a sense, when calculating the 1st unknown value, the last known has highest weight. Then the penultimate known and so forth.`}</p>
    <p>{`The algorithm is pretty simple in `}<a parentName="p" {...{
        "href": "http://haskell.org",
        "title": "Haskell (programming language)"
      }}>{`Haskell`}</a></p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`-- ((n-i)*2)/((n-1)*n)
weights :: (Num b, Fractional b, Enum b) => b -> [b]
weights n =
  map (\\i -> ((n-i)*2)/((n-1)*n)) [1.0..n]

rotate :: Int -> [a] -> [a]
rotate n l = t++h
  where (h,t) = splitAt ((length l) - n) l

expected :: (Fractional a, Enum a) => Int -> [a] -> a
expected pivot xs = sum $ map (\\(w,v) -> w*v) $ zip (rotate pivot (weights $ fromIntegral $ length xs)) xs
`}</code></pre>
    <p>{`Of course someone who actually knows Haskell would write it much better.`}</p>
    <p>{`And there you have it, a simple way to talk with Future Swizec. There is still some tweaking to do and hopefully I can find more people to run this on and see how it works out for them as well.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.roadrunnerrecords.com/blabbermouth.net/news.aspx?mode=Article&newsitemID=167732"
        }}>{`DEVIN TOWNSEND: 'I'm More On Autopilot Than Ever Before Now'`}</a>{` (roadrunnerrecords.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://swizec.com/blog/sabbatical-week-day-3-raining-datatypes/swizec/3293"
        }}>{`Sabbatical week day 3: Raining datatypes`}</a>{` (swizec.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://news.ycombinator.com/item?id=3428195"
        }}>{`Ask HN: What's your experience with remote working? as employees/employers?`}</a>{` (news.ycombinator.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://swizec.com/blog/making-a-mobius-cake/ponywithhiccups/2737"
        }}>{`Making a Möbius cake`}</a>{` (swizec.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/8664411/haskell-the-last-statement-in-a-do-construct-must-be-an-expression"
        }}>{`Haskell : "The last statement in a 'do' construct must be an expression"`}</a>{` (stackoverflow.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=44227b78-3df1-44c8-9c91-6dde805ece94",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      